import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Server, Cloud, Database, Puzzle, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { V2SubscriptionForm } from '@/components/schools/V2SubscriptionForm';
import type { WizardFormData, DeploymentFormData } from '../types';
import type { SchoolOptions, CreateSchoolResponse } from '@/types/school';

interface DeploymentStepProps {
  formData: WizardFormData;
  onChange: (data: Partial<WizardFormData>) => void;
  options: SchoolOptions | null;
  isDeploying?: boolean;
  creationResult?: CreateSchoolResponse | null;
  creationProgress?: {
    database: boolean | null;
    folder: boolean | null;
    informations: boolean | null;
  };
}

export function DeploymentStep({
  formData,
  onChange,
  options,
  isDeploying = false,
  creationResult,
  creationProgress,
}: DeploymentStepProps) {
  const handleV1Change = (data: Partial<DeploymentFormData>) => {
    onChange({
      deployV1: { ...formData.deployV1, ...data },
    });
  };

  const handleV2Change = (data: Partial<DeploymentFormData>) => {
    onChange({
      deployV2: { ...formData.deployV2, ...data },
    });
  };

  const handleModuleToggle = (moduleId: number) => {
    const currentModules = formData.deployV1.moduleIds || [];
    const newModules = currentModules.includes(moduleId)
      ? currentModules.filter((id) => id !== moduleId)
      : [...currentModules, moduleId];
    handleV1Change({ moduleIds: newModules });
  };

  const noDeploymentSelected = !formData.deployV1.enabled && !formData.deployV2.enabled;

  // V1 workflow progress component
  const V1WorkflowProgress = ({ workflow }: { workflow?: { database: boolean; folder: boolean; informations: boolean } }) => {
    const steps = [
      { key: 'database', label: 'Creation de la base de donnees', status: workflow?.database },
      { key: 'folder', label: 'Creation du dossier', status: workflow?.folder },
      { key: 'informations', label: 'Configuration des informations', status: workflow?.informations },
    ];

    return (
      <div className="space-y-3">
        {steps.map((step) => (
          <div key={step.key} className="flex items-center gap-3">
            {step.status === undefined && (
              <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
            )}
            {step.status === true && (
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            )}
            {step.status === false && (
              <AlertCircle className="h-5 w-5 text-red-500" />
            )}
            <span className="text-sm">{step.label}</span>
          </div>
        ))}
      </div>
    );
  };

  // Show V1 deployment progress when deploying (V2 is handled by V2DeployDialog)
  if ((isDeploying || creationProgress) && formData.deployV1.enabled) {
    const v1Workflow = creationResult?.workflow?.v1?.workflow;

    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-xl font-semibold text-foreground">
            Deploiement V1 en cours...
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Veuillez patienter pendant le déploiement de l'école <strong>{formData.name}</strong>
          </p>
        </div>

        <div className="grid gap-6 grid-cols-1 max-w-lg mx-auto">
          <Card className="border-blue-200">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Server className="h-5 w-5 text-blue-600" />
                </div>
                <CardTitle className="text-lg">Version 1</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <V1WorkflowProgress workflow={v1Workflow || creationProgress as any} />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Show deployment selection form
  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-xl font-semibold text-foreground">Deploiement</h2>
        <p className="text-sm text-muted-foreground mt-1">
          L'école <strong>{formData.name}</strong> a été créée. Choisissez sur quelle(s) version(s) la déployer.
        </p>
      </div>

      {noDeploymentSelected && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Vous pouvez passer cette etape et deployer l'ecole plus tard depuis sa fiche.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* V1 Deployment Card */}
        <Card className={`transition-all ${formData.deployV1.enabled ? 'border-blue-500 shadow-md' : 'opacity-75'}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${formData.deployV1.enabled ? 'bg-blue-100' : 'bg-muted'}`}>
                  <Server className={`h-5 w-5 ${formData.deployV1.enabled ? 'text-blue-600' : 'text-muted-foreground'}`} />
                </div>
                <CardTitle className="text-lg">Version 1</CardTitle>
              </div>
              <Switch
                checked={formData.deployV1.enabled}
                onCheckedChange={(checked) => handleV1Change({ enabled: checked })}
              />
            </div>
          </CardHeader>

          {formData.deployV1.enabled && (
            <CardContent className="space-y-4 pt-0">
              {/* Status */}
              <div className="space-y-2">
                <Label>Statut initial</Label>
                <Select
                  value={formData.deployV1.status}
                  onValueChange={(value) => handleV1Change({ status: value as DeploymentFormData['status'] })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selectionner un statut" />
                  </SelectTrigger>
                  <SelectContent>
                    {options?.statuses.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Source School */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  École source
                </Label>
                <Select
                  value={formData.deployV1.schoolSourceId?.toString() || 'default'}
                  onValueChange={(value) =>
                    handleV1Change({ schoolSourceId: value === 'default' ? null : parseInt(value) })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une école source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Par defaut</SelectItem>
                    {options?.sourceSchools.map((school) => (
                      <SelectItem key={school.id} value={school.id.toString()}>
                        {school.name} ({school.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Modules */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Puzzle className="h-4 w-4" />
                  Modules
                </Label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-md p-2">
                  {options?.modules.filter(m => !m.isV2).map((module) => (
                    <div key={module.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`v1-module-${module.id}`}
                        checked={formData.deployV1.moduleIds.includes(module.id)}
                        onCheckedChange={() => handleModuleToggle(module.id)}
                      />
                      <Label
                        htmlFor={`v1-module-${module.id}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {module.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* V2 Deployment Card */}
        <Card className={`transition-all ${formData.deployV2.enabled ? 'border-green-500 shadow-md' : 'opacity-75'}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${formData.deployV2.enabled ? 'bg-green-100' : 'bg-muted'}`}>
                  <Cloud className={`h-5 w-5 ${formData.deployV2.enabled ? 'text-green-600' : 'text-muted-foreground'}`} />
                </div>
                <CardTitle className="text-lg">Version 2</CardTitle>
              </div>
              <Switch
                checked={formData.deployV2.enabled}
                onCheckedChange={(checked) => handleV2Change({ enabled: checked })}
              />
            </div>
          </CardHeader>

          {formData.deployV2.enabled && (
            <CardContent className="pt-0">
              <V2SubscriptionForm
                options={options}
                value={{
                  offerId: formData.deployV2.offerId,
                  optionIds: formData.deployV2.optionIds,
                }}
                onChange={(data) => {
                  handleV2Change({
                    offerId: data.offerId,
                    optionIds: data.optionIds,
                  });
                }}
                showTenantGroup={false}
              />
            </CardContent>
          )}
        </Card>
      </div>

      {/* Summary of selection */}
      <div className="text-center text-sm text-muted-foreground mt-6">
        {noDeploymentSelected ? (
          <p>Cliquez sur "Voir la fiche" pour terminer sans deploiement.</p>
        ) : (
          <p>
            L'école sera déployée sur :{' '}
            <span className="font-medium text-foreground">
              {[
                formData.deployV1.enabled && 'V1',
                formData.deployV2.enabled && 'V2',
              ]
                .filter(Boolean)
                .join(' et ')}
            </span>
          </p>
        )}
      </div>
    </div>
  );
}
